import { useEffect, useMemo, useState } from 'react';
import { fournisseurConnexion, type Connexion } from '@taki/data-model';
import { LIA_MODEL_CHOICES, LIA_MODEL_DEFAULT, numeroLiaLisible, WHATSAPP_NUMERO_LIA, type TakiSdk } from '@taki/sdk';
import type { ConnecteurReglagesView, ConnecteursPort, ConnecteurView, IaSettingsPort, WhatsappLienView, WhatsappPort } from '@taki/ui';

/** Lia par WhatsApp : le lien de la personne et le canal de l'org, en temps reel, et les trois gestes. */
export function useWhatsappDeLHote(sdk: TakiSdk, email: string | null): WhatsappPort {
  const [lien, setLien] = useState<WhatsappLienView | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (email === null) { setLien(null); setReady(false); return; }
    setReady(false);
    const stopLien = sdk.whatsapp.observerLien((value) => { setLien(value); setReady(true); }, () => { setLien(null); setReady(true); });
    return () => { stopLien(); };
  }, [sdk, email]);

  return useMemo(() => ({
    ready,
    lien,
    numeroLia: WHATSAPP_NUMERO_LIA,
    numeroLiaLisible: numeroLiaLisible(),
    appairer: () => sdk.whatsapp.appairer(),
    delier: async () => { await sdk.whatsapp.delier(); },
    setNotificationsPersonnelles: async (value: boolean) => { if (lien) await sdk.whatsapp.setNotificationsPersonnelles(lien.phone, value); },
  }), [ready, lien, sdk]);
}

/**
 * LES CONNECTEURS DU MEMBRE : les connexions du socle (`sdk.connexions`, les
 * memes que l'Atelier de Flux) dont le fournisseur se relie par OAuth, les
 * SIENNES seulement, dans l'ordre ou il les a ajoutees (l'ordre de
 * modification ferait sauter une ligne qu'on regle).
 */
export function vuesDesConnecteurs(connexions: readonly Connexion[], uid: string): ConnecteurView[] {
  const temps = (date: unknown): number => (date instanceof Date ? date.getTime() : 0);
  return connexions
    .filter((c) => fournisseurConnexion(c.fournisseur)?.authentification === 'oauth2' && c.auteurUid === uid)
    .sort((a, b) => temps(a.createdAt) - temps(b.createdAt))
    .map((c) => ({
      id: c.id,
      fournisseur: c.fournisseur,
      nom: c.nom,
      compte: c.compte ?? '',
      acces: c.acces,
      usage: c.usage ?? 'lia',
      statut: c.statut ?? 'active',
      selection: c.selection ?? [],
      derniereUtilisation: c.derniereUtilisation instanceof Date ? c.derniereUtilisation : null,
    }));
}

/** Les cles posees seulement : une valeur `undefined` ne part pas au serveur. */
function sansVides(donnees: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(donnees).filter(([, valeur]) => valeur !== undefined));
}

/**
 * Les connecteurs mail et agenda : lus en temps reel, gestes par la callable des
 * connexions (`sdk.connexions.appeler` : `fournisseurs`, `consentir`, `modifier`,
 * `choix`, `supprimer`), la meme que l'Atelier de Flux. Un geste qui part chez le
 * fournisseur rend une adresse : le navigateur y va, et revient sur Parametres.
 */
export function useConnecteursDeLHote(sdk: TakiSdk, uid: string | null): ConnecteursPort {
  const [connexions, setConnexions] = useState<readonly ConnecteurView[]>([]);
  const [ready, setReady] = useState(false);
  const [configures, setConfigures] = useState<ReadonlySet<string> | null>(null);

  useEffect(() => {
    if (uid === null) { setConnexions([]); setReady(false); return; }
    setReady(false);
    return sdk.connexions.observer(
      (liste) => { setConnexions(vuesDesConnecteurs(liste, uid)); setReady(true); },
      () => { setConnexions([]); setReady(true); },
    );
  }, [sdk, uid]);

  useEffect(() => {
    if (uid === null) { setConfigures(null); return; }
    let vivant = true;
    sdk.connexions.appeler<{ configures?: unknown }>('fournisseurs').then(
      (reponse) => {
        const ids = Array.isArray(reponse?.configures) ? reponse.configures.filter((id): id is string => typeof id === 'string') : [];
        if (vivant) setConfigures(new Set(ids));
      },
      /* On ne sait pas : rien n'est grise, le serveur tranchera au clic. */
      () => { if (vivant) setConfigures(null); },
    );
    return () => { vivant = false; };
  }, [sdk, uid]);

  return useMemo(() => {
    const partir = (reponse: { url?: unknown } | null | undefined): void => {
      if (typeof reponse?.url === 'string' && reponse.url !== '') window.location.assign(reponse.url);
    };
    return {
      ready,
      connexions,
      configures,
      connecter: async (fournisseur: string, options: { acces?: string; connexionId?: string } = {}) => {
        partir(await sdk.connexions.appeler<{ url?: unknown }>('consentir',
          sansVides({ fournisseur, acces: options.acces, id: options.connexionId, retour: window.location.origin })));
      },
      regler: async (id: string, reglages: ConnecteurReglagesView) => {
        partir(await sdk.connexions.appeler<{ ok?: boolean; url?: unknown }>('modifier',
          sansVides({ id, ...reglages, retour: window.location.origin })));
      },
      choix: async (id: string) => {
        const reponse = await sdk.connexions.appeler<{ options?: { id: string; nom: string; principal?: boolean }[] }>('choix', { id });
        return (reponse?.options ?? []).map((o) => ({ id: o.id, nom: o.nom, principal: o.principal === true }));
      },
      retirer: async (id: string) => {
        const reponse = await sdk.connexions.appeler<{ ok?: boolean; revoque?: boolean }>('supprimer', { id });
        return { revoque: reponse?.revoque === true };
      },
    };
  }, [ready, connexions, configures, sdk]);
}

/** L'hote relie le SDK au shell sans donner Firebase aux ecrans du socle. */
export function useIaSettingsDeLHote(sdk: TakiSdk, email: string | null, canManage: boolean, uid: string | null): IaSettingsPort {
  const whatsapp = useWhatsappDeLHote(sdk, email);
  const connecteurs = useConnecteursDeLHote(sdk, uid);
  const [enabled, setEnabled] = useState(false);
  const [launcher, setLauncher] = useState<IaSettingsPort['launcher']>('mascot');
  const [model, setModel] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (email === null) {
      setEnabled(false);
      setLauncher('mascot');
      setModel(null);
      setReady(false);
      return;
    }
    setReady(false);
    return sdk.iaSettings.observer(
      (settings) => { setEnabled(settings.enabled); setLauncher(settings.launcher); setModel(settings.model); setReady(true); },
      () => { setEnabled(false); setLauncher('mascot'); setModel(null); setReady(true); },
    );
  }, [sdk, email]);

  return useMemo(() => ({
    enabled,
    launcher,
    model,
    models: LIA_MODEL_CHOICES,
    recordStatus: async (family: string, id: string) => {
      const repository = (sdk as unknown as Record<string, { get?: (id: string) => Promise<{ archivedAt?: unknown; deletedAt?: unknown } | null> } | undefined>)[family];
      if (typeof repository?.get !== 'function') return 'unknown' as const;
      try {
        const record = await repository.get(id);
        if (record == null) return 'missing' as const;
        return record.deletedAt != null ? 'deleted' as const : record.archivedAt != null ? 'archived' as const : 'active' as const;
      } catch { return 'inaccessible' as const; }
    },
    defaultModel: LIA_MODEL_DEFAULT,
    ready,
    canManage,
    setEnabled: (value: boolean) => sdk.iaSettings.setEnabled(value),
    setLauncher: (value: IaSettingsPort['launcher']) => sdk.iaSettings.setLauncher(value),
    setModel: (value: string | null) => sdk.iaSettings.setModel(value),
    connections: (includeAll = false) => sdk.iaSettings.connections(includeAll && canManage),
    revokeConnection: (id: string) => sdk.iaSettings.revokeConnection(id),
    whatsapp,
    connecteurs,
  }), [enabled, launcher, model, ready, canManage, sdk, whatsapp, connecteurs]);
}
